// ShiftPicker.jsx
// "Which booth is this?" — the ONLY thing that resolves an ambiguous shift.
//
// Renders ONLY when two or more shifts are active at once and nothing (URL
// param, device binding) already answered the question. On an ordinary
// one-queue day this component never mounts. See utils/shiftScope.js.
//
// Two audiences, one component, because the decision is the same decision:
//   staff — "which queue is this device working?", persists a device binding
//   guest — "which booth are you standing at?", one-shot, no binding written
//
// Deliberately NOT dismissible for staff: a device with no resolved shift
// cannot safely take an order, and a dismissed prompt would leave it silently
// pointed at nothing.
import React, { useState } from "react";
import { STAFF_THEME, FONT } from "./../theme/staff";
import { guestSafeLabel, isCompleteJoinCode, normalizeJoinCode, shiftLabel } from "./../utils/shiftScope";

const BRAND = STAFF_THEME;

export default function ShiftPicker({
  candidates = [],
  onPick,
  audience = "staff",
  title,
  subtitle,
  allowCode = true,
}) {
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const isGuest = audience === "guest";
  // Staff see the real event name; guests see only what may be public.
  const labelFor = isGuest ? guestSafeLabel : shiftLabel;

  const heading = title || (isGuest ? "Which booth are you at?" : "Which queue is this device?");
  const sub = subtitle || (isGuest
    ? "We're at more than one event right now — pick yours so your pancake lands on the right griddle."
    : "Two queues are open. Pick the one this device is working. You can change it later from the queue header.");

  function submitCode(e) {
    e?.preventDefault?.();
    const norm = normalizeJoinCode(code);
    const match = candidates.find(s => normalizeJoinCode(s.joinCode) === norm);
    if (!match) { setCodeError("No booth with that code. Check the screen and try again."); return; }
    setCodeError("");
    onPick?.(match);
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9000,
      background: "rgba(13,24,51,0.92)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, fontFamily: FONT.body,
    }}>
      <div style={{
        width: "100%", maxWidth: 420,
        background: BRAND.panel, border: `1px solid ${BRAND.border}`,
        borderRadius: 18, padding: 24,
        boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
      }}>
        <div style={{ fontSize: 34, textAlign: "center", marginBottom: 6 }}>🥞</div>
        <h2 style={{
          margin: "0 0 8px", textAlign: "center",
          fontFamily: FONT.display, fontSize: 22, color: BRAND.text,
        }}>{heading}</h2>
        <p style={{
          margin: "0 0 20px", textAlign: "center",
          fontSize: 14, lineHeight: 1.45, color: BRAND.muted,
        }}>{sub}</p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {candidates.map(s => (
            <button
              key={s.id}
              onClick={() => onPick?.(s)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 12, width: "100%", padding: "14px 16px",
                background: BRAND.card, border: `1px solid ${BRAND.border}`,
                borderRadius: 12, color: BRAND.text,
                fontFamily: FONT.display, fontSize: 16, textAlign: "left",
                cursor: "pointer",
              }}
            >
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {labelFor(s)}
              </span>
              {s.joinCode && (
                <span style={{
                  flexShrink: 0, padding: "3px 8px", borderRadius: 7,
                  background: "rgba(252,179,31,0.15)", color: BRAND.gold,
                  fontSize: 12, letterSpacing: 1.5, fontWeight: 600,
                }}>{s.joinCode}</span>
              )}
            </button>
          ))}
        </div>

        {allowCode && (
          <form onSubmit={submitCode} style={{ marginTop: 18 }}>
            <label style={{
              display: "block", marginBottom: 6,
              fontSize: 12, color: BRAND.dim, textAlign: "center",
            }}>
              or enter the booth code shown on the screen
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                value={code}
                onChange={(e) => { setCode(normalizeJoinCode(e.target.value)); setCodeError(""); }}
                placeholder="ABCD"
                inputMode="text"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                maxLength={4}
                style={{
                  flex: 1, minWidth: 0, padding: "11px 14px",
                  background: BRAND.card, border: `1px solid ${BRAND.border}`,
                  borderRadius: 10, color: BRAND.text,
                  fontFamily: FONT.display, fontSize: 18,
                  letterSpacing: 5, textAlign: "center", textTransform: "uppercase",
                }}
              />
              <button
                type="submit"
                disabled={!isCompleteJoinCode(code)}
                style={{
                  flexShrink: 0, padding: "11px 18px",
                  background: isCompleteJoinCode(code) ? BRAND.blue : BRAND.card,
                  border: "none", borderRadius: 10,
                  color: isCompleteJoinCode(code) ? "#fff" : BRAND.dim,
                  fontFamily: FONT.display, fontSize: 15,
                  cursor: isCompleteJoinCode(code) ? "pointer" : "default",
                }}
              >Go</button>
            </div>
            {codeError && (
              <div style={{ marginTop: 8, fontSize: 13, color: BRAND.red, textAlign: "center" }}>
                {codeError}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

// ── Header chip ──────────────────────────────────────────────────────────────
// Persistent "you are working THIS queue" badge for staff surfaces. Renders
// only while concurrent queues exist, so it adds no chrome on a normal day —
// but when it matters, a wrong binding is visible at a glance instead of
// being discovered three orders later.
export function ShiftChip({ shift, isConcurrent, onChange, style }) {
  if (!shift || !isConcurrent) return null;
  return (
    <button
      onClick={onChange}
      title="Change which queue this device is working"
      style={{
        display: "inline-flex", alignItems: "center", gap: 7,
        padding: "5px 10px", borderRadius: 999,
        background: "rgba(252,179,31,0.14)",
        border: "1px solid rgba(252,179,31,0.45)",
        color: BRAND.gold, fontFamily: FONT.display, fontSize: 13,
        cursor: "pointer",
        // minWidth:0 lets flex compress the chip instead of shoving siblings
        // off a 375px screen — on an iPhone the event name ellipses away and
        // the booth CODE (the part that actually disambiguates) survives.
        minWidth: 0, maxWidth: 200, ...style,
      }}
    >
      <span style={{ fontSize: 11 }}>📍</span>
      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {shiftLabel(shift)}
      </span>
      {shift.joinCode && <span style={{ opacity: 0.75, letterSpacing: 1.2, flexShrink: 0 }}>{shift.joinCode}</span>}
    </button>
  );
}
